// @ts-nocheck
import React, { useEffect, useMemo, useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import { DoubleSide, Group, LinearFilter, ClampToEdgeWrapping, MeshLambertMaterial } from 'three';
import { useTexture, Billboard } from '@react-three/drei';

type SkyboxProps = {
  isPlaying: boolean;
  size?: number;
  spin?: number;
  /** number of smoke planes */
  count?: number;
  /** smoke plane size */
  planeSize?: number;
  /** smoke color tint */
  color?: string;
  /** texture URL for smoke sprite */
  textureUrl?: string;
};

export default function Skybox({
  isPlaying,
  size = 200,
  spin = 0.25,
  count = 140,
  planeSize = 0.4,
  color = 'rgb(209, 213, 219)',
  textureUrl = 'https://s3-us-west-2.amazonaws.com/s.cdpn.io/95637/Smoke-Element.png',
}: SkyboxProps) {
  const groupRef = useRef<Group | null>(null);
  const smokeTexture = useTexture(textureUrl);
  const materialsRef = useRef<MeshLambertMaterial[]>([]);
  const opacityRef = useRef(isPlaying ? 0.4 : 0.6);

  // Optimize texture sampling (no mips, linear filtering) to reduce cost
  useEffect(() => {
    if (!smokeTexture) return;
    smokeTexture.generateMipmaps = false;
    smokeTexture.minFilter = LinearFilter;
    smokeTexture.magFilter = LinearFilter;
    smokeTexture.wrapS = ClampToEdgeWrapping;
    smokeTexture.wrapT = ClampToEdgeWrapping;
    smokeTexture.needsUpdate = true;
  }, [smokeTexture]);

  // Precompute random positions and rotations for smoke planes inside a sphere around the camera
  const planes = useMemo(() => {
    const entries: Array<{
      position: [number, number, number];
      rotationZ: number;
      scale: number;
      tiltX: number;
      tiltY: number;
    }> = [];
    // Floor-like fog distribution: place most planes near a ground band (y slightly below 0).
    // Keep additional clearance based on plane size so billboards don't visually sweep into the model.
    const planeWorldSize = size * planeSize;
    const exclusionRadius = 6; // base clear space around the model at origin
    const minClearance = exclusionRadius + planeWorldSize * 0.6; // buffer accounts for half-size + margin
    const innerR = Math.max(minClearance, size * 0.45);
    const outerR = Math.max(innerR + Math.max(8, planeWorldSize * 0.2), size * 0.65);
    const maxTilt = 0.15; // keep relatively flat to read as floor fog
    for (let i = 0; i < count; i++) {
      // Polar distribution in XZ ring
      const theta = Math.random() * Math.PI * 2;
      // Bias towards inner radius slightly to keep density near center
      const t = Math.random();
      let r = innerR * (1 - t) + outerR * t * (0.85 + Math.random() * 0.15);
      // Ensure we never get inside the clearance radius
      if (r < minClearance + 1) r = minClearance + 1 + Math.random() * 2;
      const x = r * Math.cos(theta);
      const z = r * Math.sin(theta);
      // Lower ground band to avoid intersecting model: y ~ [-2.0, -1.0]
      const y = -2.0 + Math.random() * 1.0;
      const rotationZ = Math.random() * Math.PI * 2;
      const scale = 0.9 + Math.random() * 1.8;
      const tiltX = (Math.random() * 2 - 1) * maxTilt;
      const tiltY = (Math.random() * 2 - 1) * maxTilt;
      entries.push({ position: [x, y, z], rotationZ, scale, tiltX, tiltY });
    }
    return entries;
  }, [count, size]);

  // Ensure skybox renders behind the model and doesn't visually cut through
  useEffect(() => {
    if (groupRef.current) {
      groupRef.current.renderOrder = -1;
    }
  }, []);

  useFrame((_, delta) => {
    const g = groupRef.current;
    if (!g) return;
    // Gentle global rotation plus per-plane roll (only when playing)
    if (isPlaying) {
      g.rotation.y += spin * 0.05 * delta;
    }
    // Smoothly damp opacity toward target to avoid harsh jumps
    const targetOpacity = isPlaying ? 0.4 : 0.9;
    const lambda = 1 - Math.exp(-delta / 1.0); // ~1s time constant
    opacityRef.current += (targetOpacity - opacityRef.current) * lambda;
    const mats = materialsRef.current;
    for (let i = 0; i < mats.length; i++) {
      const m = mats[i];
      if (m) {
        m.opacity = opacityRef.current;
      }
    }
  });

  const planeWorldSize = size * planeSize;

  return (
    <group ref={groupRef} frustumCulled={false}>
      {planes.map((p, idx) => (
        <Billboard key={idx} position={p.position} follow>
          <group rotation={[p.tiltX, p.tiltY, 0]}>
            <mesh rotation={[0, 0, p.rotationZ]} scale={p.scale}>
              <planeGeometry args={[planeWorldSize, planeWorldSize]} />
              <meshLambertMaterial
                ref={(m) => {
                  if (m && !materialsRef.current.includes(m)) {
                    materialsRef.current.push(m);
                  }
                }}
                color={color}
                map={smokeTexture}
                transparent
                depthWrite={false}
                toneMapped={false}
                side={DoubleSide}
                opacity={opacityRef.current}
              />
            </mesh>
          </group>
        </Billboard>
      ))}
    </group>
  );
}


